import React from 'react';
import { Layout } from '../Layout';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getApiHeader } from 'src/utils';
import { Badge, Box, Button } from '@cloudscape-design/components';
import SpaceBetween from '@cloudscape-design/components/space-between';

interface LeaderboardRecord {
    Festival_Day: string;
    CompetitionType: string;
    EventID: number;
    EventCategoryName: string;
    EventName: string;
    EventAgeGrade: string;
    First_Place: string;
    Second_Place: string;
    Third_Place: string;
    EventStatus: string;
    EventActualEndDatetime: string;
    EventAgeGradeSequence: number;
    StageName: string;
}

interface CombinedLeaderboardResponse {
    totalRecords: number;
    queryTimestamp: string;
    status: string;
    message: string;
    metadata: {
        festivalDays: string[];
        competitionTypes: string[];
        totalEvents: number;
        completedEvents: number;
        pendingEvents: number;
    };
    leaderboardRecords: LeaderboardRecord[];
}

export const CombinedLeaderboard: React.FC = () => {
    const headers = getApiHeader();

    const { data: combinedLeaderboardData, isPending } = useQuery<CombinedLeaderboardResponse>({
        queryKey: ['combinedLeaderboard'],
        queryFn: async () => {
            const result = await axios.get('https://omtrmjjoc1.execute-api.us-east-1.amazonaws.com/Prod/leaderboard/combined', {
                headers
            });
            return result.data;
        },
    });

    const exportToCSV = () => {
        if (!combinedLeaderboardData?.leaderboardRecords) return;

        const headers = [
            'Festival Day',
            'Event Name', 
            'Competition Type',
            'Age Grade',
            'First Place',
            'Second Place', 
            'Third Place',
            'Event ID',
            'Stage',
            'Event Status',
            'End Time'
        ];

        const csvContent = [
            headers.join(','),
            ...combinedLeaderboardData.leaderboardRecords.map(record => [
                record.Festival_Day || '',
                `"${record.EventName || ''}"`,
                record.CompetitionType || '',
                `"${record.EventAgeGrade || ''}"`,
                `"${record.First_Place || ''}"`,
                `"${record.Second_Place || ''}"`,
                `"${record.Third_Place || ''}"`,
                record.EventID || '',
                `"${record.StageName || ''}"`,
                record.EventStatus || '',
                record.EventActualEndDatetime || ''
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `combined-leaderboard-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const printTable = () => {
        if (!combinedLeaderboardData?.leaderboardRecords) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const tableHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Combined Leaderboard</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    h1 { color: #333; text-align: center; }
                    .metadata { margin: 20px 0; text-align: center; }
                    .metadata span { margin: 0 10px; padding: 5px 10px; background: #f0f0f0; border-radius: 3px; }
                    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; font-weight: bold; }
                    tr:nth-child(even) { background-color: #f9f9f9; }
                    @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <h1>Combined Leaderboard</h1>
                <div class="metadata">
                    <span>Festival Days: ${combinedLeaderboardData.metadata.festivalDays.join(', ')}</span>
                    <span>Total Events: ${combinedLeaderboardData.metadata.totalEvents}</span>
                    <span>Completed Events: ${combinedLeaderboardData.metadata.completedEvents}</span>
                    <span>Pending Events: ${combinedLeaderboardData.metadata.pendingEvents}</span>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Event Name</th>
                            <th>Age Grade</th>
                            <th>First Place</th>
                            <th>Second Place</th>
                            <th>Third Place</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${combinedLeaderboardData.leaderboardRecords.map(record => `
                            <tr>
                                <td>${record.EventName || '-'}</td>
                                <td>${record.EventAgeGrade || '-'}</td>
                                <td>${record.First_Place || '-'}</td>
                                <td>${record.Second_Place || '-'}</td>
                                <td>${record.Third_Place || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <script>
                    window.onload = function() {
                        window.print();
                        window.onafterprint = function() {
                            window.close();
                        };
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(tableHTML);
        printWindow.document.close();
    };

    return (
        <Layout breadcrumbs={[
            {
                href: '/combined-leaderboard',
                text: 'Combined Leaderboard'
            }
        ]}>
            <SpaceBetween direction="vertical" size="l">
                {combinedLeaderboardData && (
                    <Box>
                        <SpaceBetween direction="horizontal" size="s">
                            <Badge color="blue">Festival Days: {combinedLeaderboardData.metadata.festivalDays.join(', ')}</Badge>
                            <Badge color="green">Total Events: {combinedLeaderboardData.metadata.totalEvents}</Badge>
                            <Badge color="green">Completed Events: {combinedLeaderboardData.metadata.completedEvents}</Badge>
                            <Badge color="red">Pending Events: {combinedLeaderboardData.metadata.pendingEvents}</Badge>
                        </SpaceBetween>
                    </Box>
                )}
                <Table
                    columnDefinitions={[
                        {
                            id: "festivalDay",
                            header: "Festival Day",
                            cell: item => item.Festival_Day || "-",
                        },
                        {
                            id: "eventName",
                            header: "Event Name",
                            cell: item => item.EventName || "-",
                        },
                        {
                            id: "competitionType",
                            header: "Competition Type",
                            cell: item => item.CompetitionType || "-",
                        },
                        {
                            id: "eventAgeGrade",
                            header: "Age Grade",
                            cell: item => item.EventAgeGrade || "-"
                        },
                        {
                            id: "firstPlace",
                            header: "First Place",
                            cell: item => item.First_Place || "-"
                        },
                        {
                            id: "secondPlace",
                            header: "Second Place",
                            cell: item => item.Second_Place || "-"
                        },
                        {
                            id: "thirdPlace",
                            header: "Third Place",
                            cell: item => item.Third_Place || "-"
                        },
                        {
                            id: "eventId",
                            header: "Event ID",
                            cell: item => item.EventID || "-",
                        },
                        {
                            id: "stageName",
                            header: "Stage",
                            cell: item => item.StageName || "-"
                        },
                        {
                            id: "eventStatus",
                            header: "Event Status",
                            cell: item => (
                                <Badge color={item.EventStatus === 'Completed' ? 'green' : 'blue'}>
                                    {item.EventStatus}
                                </Badge>
                            )
                        },
                        {
                            id: "eventEndTime",
                            header: "End Time",
                            cell: item => item.EventActualEndDatetime || "-"
                        }
                    ]}
                    items={combinedLeaderboardData?.leaderboardRecords ?? []}
                    loading={isPending}
                    loadingText="Loading combined leaderboard"
                    sortingDisabled
                    header={
                        <Header
                            actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button 
                                        variant="normal" 
                                        onClick={exportToCSV}
                                        disabled={!combinedLeaderboardData?.leaderboardRecords?.length}
                                        iconName="download"
                                    >
                                        Export CSV
                                    </Button>
                                    <Button 
                                        variant="normal" 
                                        onClick={printTable}
                                        disabled={!combinedLeaderboardData?.leaderboardRecords?.length}
                                        iconName="external"
                                    >
                                        Print
                                    </Button>
                                </SpaceBetween>
                            }
                        >
                            Combined Leaderboard ({combinedLeaderboardData?.totalRecords || 0} records)
                        </Header>
                    }
                    empty={
                        <Box
                            margin={{ vertical: "xs" }}
                            textAlign="center"
                            color="inherit"
                        >
                            <SpaceBetween size="m">
                                <b>No leaderboard records found</b>
                            </SpaceBetween>
                        </Box>
                    }
                />
            </SpaceBetween>
        </Layout>
    )
}